import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { formatPrimaryName } from 'utils/names'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { ServicePlanFormData } from 'components/Assessments/ServicePlanAssessment/types/ServicePlanFormData'
import { ServicePlanFormField } from 'components/Assessments/ServicePlanAssessment/types/ServicePlanFormField'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { PatientOverviewFormField } from './formField'
import { PatientOverviewQuestionnaireItem } from './questionnaire'

/**
 * Patient Overview subsection adapter for Service Plan Assessment
 * Handles demographics and care team fields extraction and transformation
 */
export class PatientOverviewAdapter extends BaseSubsectionAdapter<ServicePlanFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Partial<ServicePlanFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Demographics fields
    const legalName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_LEGAL_NAME
      )?.at(0) || undefined

    const preferredName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_PREFERRED_NAME
      )?.at(0) || undefined

    const dateOfBirth =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_DATE_OF_BIRTH
      )?.at(0) || undefined

    const address =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_ADDRESS
      )?.at(0) || undefined

    const gender =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_GENDER
      )?.at(0) || undefined

    const sex =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_SEX
      )?.at(0) || undefined

    const medicaidId =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID
      )?.at(0) || undefined

    const ssn =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_SSN
      )?.at(0) || undefined

    const primaryLanguage =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_PRIMARY_LANGUAGE
      )?.at(0) || undefined

    const religion =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_RELIGION
      )?.at(0) || undefined

    const race =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_RACE
      )?.at(0) || undefined

    const triageCode =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_TRIAGE_CODE
      )?.at(0) || undefined

    // Care team fields
    const legalGuardianName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_NAME
      )?.at(0) || undefined

    const legalGuardianRelation =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_RELATION_TO_PATIENT
      )?.at(0) || undefined

    const orderingPractitioner =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ORDERING_PRACTITIONER
      )?.at(0) || undefined

    const primaryCareProvider =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_PRIMARY_CARE_PROVIDER
      )?.at(0) || undefined

    const orderPreference =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ORDER_PREFERENCE
      ) || undefined

    const socDate =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_SOC_DATE
      )?.at(0) || undefined

    const emergencyContactName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_NAME
      )?.at(0) || undefined

    const emergencyContactPhone =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_PHONE
      )?.at(0) || undefined

    const emergencyContactAddress =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_ADDRESS
      )?.at(0) || undefined

    const alternateCaregiverName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_NAME
      )?.at(0) || undefined

    const alternateCaregiverPhone =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_PHONE
      )?.at(0) || undefined

    const alternateCaregiverAddress =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_ADDRESS
      )?.at(0) || undefined

    // Handle caregivers array - complex mapping from care team options
    const names =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_NAME
      ) || []

    const relations =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_RELATION_TO_PATIENT
      ) || []

    let caregivers: Array<{ name: string; relation: string }> | undefined

    if (options?.careTeam?.caregivers?.length) {
      caregivers = options.careTeam.caregivers
        .map((caregiver, index) => ({
          name: formatPrimaryName(caregiver.name) || names[index] || undefined,
          relation: relations[index] || undefined
        }))
        .filter((caregiver) => caregiver.name || caregiver.relation)
    } else if (names.length > 0 || relations.length > 0) {
      // Fallback: use indexed fields if careTeam not available
      const maxLength = Math.max(names.length, relations.length)
      caregivers = Array.from({ length: maxLength }, (_, index) => ({
        name: names[index] || undefined,
        relation: relations[index] || undefined
      })).filter((caregiver) => caregiver.name || caregiver.relation)
    }

    return {
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_LEGAL_NAME]: legalName,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_PREFERRED_NAME]: preferredName,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_DATE_OF_BIRTH]: dateOfBirth,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_ADDRESS]: address,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_GENDER]: gender,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_SEX]: sex,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID]: medicaidId,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_SSN]: ssn,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_PRIMARY_LANGUAGE]: primaryLanguage,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_RELIGION]: religion,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_RACE]: race,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_TRIAGE_CODE]: triageCode,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_NAME]: legalGuardianName,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_RELATION_TO_PATIENT]:
        legalGuardianRelation,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_ORDERING_PRACTITIONER]:
        orderingPractitioner,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_PRIMARY_CARE_PROVIDER]:
        primaryCareProvider,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_ORDER_PREFERENCE]: orderPreference,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_SOC_DATE]: socDate,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_NAME]:
        emergencyContactName,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_PHONE]:
        emergencyContactPhone,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_ADDRESS]:
        emergencyContactAddress,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_NAME]:
        alternateCaregiverName,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_PHONE]:
        alternateCaregiverPhone,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_ADDRESS]:
        alternateCaregiverAddress,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER]: caregivers
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_LEGAL_NAME)) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_LEGAL_NAME,
        value: this.getFormDataField(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_LEGAL_NAME)
      })
    }

    if (
      this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_PREFERRED_NAME)
    ) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_PREFERRED_NAME,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_PREFERRED_NAME
        )
      })
    }

    if (
      this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_DATE_OF_BIRTH)
    ) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_DATE_OF_BIRTH,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_DATE_OF_BIRTH
        )
      })
    }

    if (this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_ADDRESS)) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_ADDRESS,
        value: this.getFormDataField(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_ADDRESS)
      })
    }

    if (this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_GENDER)) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_GENDER,
        value: this.getFormDataField(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_GENDER)
      })
    }

    if (this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_SEX)) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_SEX,
        value: this.getFormDataField(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_SEX)
      })
    }

    if (this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID)) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID,
        value: this.getFormDataField(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID)
      })
    }

    if (this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_SSN)) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_SSN,
        value: this.getFormDataField(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_SSN)
      })
    }

    if (
      this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_PRIMARY_LANGUAGE)
    ) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_PRIMARY_LANGUAGE,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_PRIMARY_LANGUAGE
        )
      })
    }

    if (this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_RELIGION)) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_RELIGION,
        value: this.getFormDataField(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_RELIGION)
      })
    }

    if (this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_RACE)) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_RACE,
        value: this.getFormDataField(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_RACE)
      })
    }

    if (this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_TRIAGE_CODE)) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_TRIAGE_CODE,
        value: this.getFormDataField(ServicePlanFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_TRIAGE_CODE)
      })
    }

    if (
      this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_NAME)
    ) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_NAME,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_NAME
        )
      })
    }

    if (
      this.formDataFieldExists(
        ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_RELATION_TO_PATIENT
      )
    ) {
      mutations.push({
        linkId:
          PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_RELATION_TO_PATIENT,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_RELATION_TO_PATIENT
        )
      })
    }

    if (
      this.formDataFieldExists(
        ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_ORDERING_PRACTITIONER
      )
    ) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ORDERING_PRACTITIONER,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_ORDERING_PRACTITIONER
        )
      })
    }

    if (
      this.formDataFieldExists(
        ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_PRIMARY_CARE_PROVIDER
      )
    ) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_PRIMARY_CARE_PROVIDER,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_PRIMARY_CARE_PROVIDER
        )
      })
    }

    if (
      this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_ORDER_PREFERENCE)
    ) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ORDER_PREFERENCE,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_ORDER_PREFERENCE
        )
      })
    }

    if (this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_SOC_DATE)) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_SOC_DATE,
        value: this.getFormDataField(ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_SOC_DATE)
      })
    }

    if (
      this.formDataFieldExists(
        ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_NAME
      )
    ) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_NAME,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_NAME
        )
      })
    }

    if (
      this.formDataFieldExists(
        ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_PHONE
      )
    ) {
      mutations.push({
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_PHONE,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_PHONE
        )
      })
    }

    if (
      this.formDataFieldExists(
        ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_ADDRESS
      )
    ) {
      mutations.push({
        linkId:
          PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_ADDRESS,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_ADDRESS
        )
      })
    }

    if (
      this.formDataFieldExists(
        ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_NAME
      )
    ) {
      mutations.push({
        linkId:
          PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_NAME,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_NAME
        )
      })
    }

    if (
      this.formDataFieldExists(
        ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_PHONE
      )
    ) {
      mutations.push({
        linkId:
          PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_PHONE,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_PHONE
        )
      })
    }

    if (
      this.formDataFieldExists(
        ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_ADDRESS
      )
    ) {
      mutations.push({
        linkId:
          PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_ADDRESS,
        value: this.getFormDataField(
          ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_ADDRESS
        )
      })
    }

    // Handle caregivers array - ensure names and relations stay aligned by index
    if (this.formDataFieldExists(ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER)) {
      const caregivers = this.getFormDataField(
        ServicePlanFormField.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER
      ) as Array<{ name: string; relation: string }> | undefined

      const validCaregivers = caregivers?.filter(
        (caregiver) => caregiver.name || caregiver.relation
      )

      if (validCaregivers && validCaregivers.length > 0) {
        mutations.push({
          linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_NAME,
          value: validCaregivers.map((caregiver) => caregiver.name || '')
        })
        mutations.push({
          linkId:
            PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_RELATION_TO_PATIENT,
          value: validCaregivers.map((caregiver) => caregiver.relation || '')
        })
      } else {
        mutations.push({
          linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_NAME,
          value: undefined
        })
        mutations.push({
          linkId:
            PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_RELATION_TO_PATIENT,
          value: undefined
        })
      }
    }

    return mutations
  }
}
